import { useSearchParams } from "react-router";
import LivenessWrapper from "@/domains/auth/components/liveness/LivenessWrapper";

// Helper function to validate redirect URL to prevent open redirect attacks
export const ALLOWED_REDIRECT_HOSTS = ['.payjoy.com', '.payjoy.io'];

// Native app deep links use a custom (non-https) scheme, so the HTTPS/host check below
// would always reject them. Matched exactly here instead, before that check runs.
export const ALLOWED_REDIRECT_DEEPLINKS = ['com.payjoy://selfie-complete'];

export const isValidRedirectUrl = (url: string): boolean => {
  // Allow relative URLs
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }
  if (ALLOWED_REDIRECT_DEEPLINKS.includes(url)) {
    return true;
  }
  try {
    const parsed = new URL(url);
    // From here on, only allow HTTPS URLs on trusted PayJoy domains (deep links are handled above)
    if (parsed.protocol !== 'https:') {
      return false;
    }
    const hostname = parsed.hostname.toLowerCase();
    return ALLOWED_REDIRECT_HOSTS.some(
      (allowed) => hostname === allowed.slice(1) || hostname.endsWith(allowed)
    );
  } catch {
    return false;
  }
};

// postMessage's targetOrigin must be a concrete absolute origin; anything else
// (a relative path, a deep link, a malformed value) throws SyntaxError. We can't
// reuse isValidRedirectUrl directly, since it also accepts relative paths and
// deep links that are valid redirects but invalid target origins. Extract the
// absolute origin and only accept it when it passes the trusted-domain allowlist.
const asTrustedOrigin = (value: string): string | null => {
  try {
    const { origin } = new URL(value);
    return isValidRedirectUrl(origin) ? origin : null;
  } catch {
    return null;
  }
};

// Resolve the origin to pass postMessage as its targetOrigin filter: the parent
// window's real origin, which for a cross-origin embed differs from the iframe's
// own origin. Prefer the explicit parent_origin the embedder passes in — a strict
// Referrer-Policy can strip document.referrer, and falling back to the iframe's
// own origin makes the browser silently drop the message (QR-login push fails).
export const resolveParentTargetOrigin = (
  parentOriginParam: string | null,
  referrer: string,
  selfOrigin: string,
): string => {
  if (parentOriginParam) {
    const fromParam = asTrustedOrigin(parentOriginParam);
    if (fromParam) {
      return fromParam;
    }
  }
  if (referrer) {
    const fromReferrer = asTrustedOrigin(referrer);
    if (fromReferrer) {
      return fromReferrer;
    }
  }
  return selfOrigin;
};

function Liveness() {
  const [searchParams] = useSearchParams();
  const callBack = (response: any) => {
    const { result, error_status, error_message, transaction_id } = response;
    // Check both 'redirect_url' and 'redirect' params
    const redirectUrl = searchParams.get("redirect_url") || searchParams.get("redirect") || '/';
    
    // Validate redirect URL to prevent open redirect attacks
    if (!isValidRedirectUrl(redirectUrl)) {
      console.error('Invalid redirect URL detected, redirecting to home');
      window.location.href = '/';
      return;
    }
    
    // Check if running in an iframe
    const isInIframe = window.self !== window.top;
    
    if (isInIframe) {
      // Target the parent's real origin. For a cross-origin embed this is NOT the
      // iframe's own origin; getting it wrong makes the browser drop the message.
      const targetOrigin = resolveParentTargetOrigin(
        searchParams.get('parent_origin'),
        document.referrer,
        window.location.origin,
      );

      // Send message to parent window to handle the redirect
      window.parent.postMessage({
        type: 'LIVENESS_COMPLETE',
        result: result,
        transaction_id: transaction_id,
        redirectUrl: redirectUrl,
        response: response
      }, targetOrigin);
    } else {
      // Parse the redirect URL to append query params from response
      const url = new URL(redirectUrl, window.location.origin);

      if (transaction_id) {
        url.searchParams.set('transaction_id', transaction_id);
      }
      // Add result status (SUCCESS or FAILED)
      if (result) {
        url.searchParams.set('result', result);
      }
      // Add error information if available
      if (error_status) {
        url.searchParams.set('error_status', error_status);
      }
      if (error_message) {
        url.searchParams.set('error_message', error_message);
      }
      window.location.href = url.toString();
    }
  }

  return (
    <LivenessWrapper callBack={callBack} personUuid={searchParams.get("person_uuid") || ''} />
  );
}
export default Liveness;